import React, { useEffect } from 'react';
import { Modal, Form, InputNumber, Row, Col } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
    EditDefaultsModalBody,
    SmartDefaultsModalEyebrow,
    SmartDefaultsModalTitle,
    SmartDefaultsModalDescription,
} from './smartDefaults.style';
import FieldWarning from './FieldWarning';
import {
    DECIMAL_NUMBER_INPUT_PROPS,
    INTEGER_NUMBER_INPUT_PROPS,
} from './numericInputProps';
import {
    getCogsWarning,
    getReplenishmentWarning,
    getTaxCollectedWarning,
    getTaxPaidOnCogsWarning,
} from './validationWarnings';

const SUFFIX_STYLE = {
    color: 'var(--app-text-muted)',
    fontWeight: 600,
    fontSize: 13,
};

const EditDefaultsModal = ({ open, defaults, saving, onCancel, onSave }) => {
    const [form] = Form.useForm();

    const cogsPercentOfPrice = Form.useWatch('cogsPercentOfPrice', form);
    const replenishmentDays = Form.useWatch('replenishmentDays', form);
    const taxPaidOnCogsPercent = Form.useWatch('taxPaidOnCogsPercent', form);
    const taxCollectedOnSalesPercent = Form.useWatch('taxCollectedOnSalesPercent', form);

    const cogsWarning = getCogsWarning(cogsPercentOfPrice);
    const replenishmentWarning = getReplenishmentWarning(replenishmentDays);
    const taxPaidWarning = getTaxPaidOnCogsWarning(taxPaidOnCogsPercent);
    const taxCollectedWarning = getTaxCollectedWarning(taxCollectedOnSalesPercent);

    useEffect(() => {
        if (open) {
            form.setFieldsValue(defaults);
        }
    }, [open, defaults, form]);

    const handleFinish = (values) => {
        onSave({
            cogsPercentOfPrice: values.cogsPercentOfPrice,
            replenishmentDays: values.replenishmentDays,
            unsellableReturnsPercent: values.unsellableReturnsPercent,
            taxPaidOnCogsPercent: values.taxPaidOnCogsPercent,
            taxCollectedOnSalesPercent: values.taxCollectedOnSalesPercent,
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={560}
            centered
            destroyOnClose
            className="smart-defaults-edit-modal"
            title={null}
        >
            <EditDefaultsModalBody>
                <SmartDefaultsModalEyebrow>Smart defaults</SmartDefaultsModalEyebrow>
                <SmartDefaultsModalTitle>Edit your defaults</SmartDefaultsModalTitle>
                <SmartDefaultsModalDescription>
                    These apply to every product you haven&apos;t customized yet. Changes roll out across your catalog.
                </SmartDefaultsModalDescription>

                <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark={false}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="cogsPercentOfPrice"
                                label={
                                    <span className="smart-defaults-modal-label">
                                        COGS{' '}
                                        <span className="smart-defaults-modal-label-hint">(% of selling price)</span>
                                    </span>
                                }
                                rules={[{ required: true, message: 'Required' }]}
                                extra={<FieldWarning message={cogsWarning} />}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    className="w-100 products-table-edit-input"
                                    suffix={<span style={SUFFIX_STYLE}>%</span>}
                                    {...INTEGER_NUMBER_INPUT_PROPS}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="replenishmentDays"
                                label={<span className="smart-defaults-modal-label">Replenishment time</span>}
                                rules={[{ required: true, message: 'Required' }]}
                                extra={<FieldWarning message={replenishmentWarning} />}
                            >
                                <InputNumber
                                    min={0}
                                    className="w-100 products-table-edit-input"
                                    suffix={<span style={SUFFIX_STYLE}>days</span>}
                                    {...INTEGER_NUMBER_INPUT_PROPS}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="unsellableReturnsPercent"
                                label={
                                    <span className="smart-defaults-modal-label">
                                        Unsellable returns{' '}
                                        <span className="smart-defaults-modal-label-hint">(% of returns)</span>
                                    </span>
                                }
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    className="w-100 products-table-edit-input"
                                    suffix={<span style={SUFFIX_STYLE}>%</span>}
                                    {...INTEGER_NUMBER_INPUT_PROPS}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="taxPaidOnCogsPercent"
                                label={<span className="smart-defaults-modal-label">Tax paid on COGS</span>}
                                rules={[{ required: true, message: 'Required' }]}
                                extra={<FieldWarning message={taxPaidWarning} />}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    className="w-100 products-table-edit-input"
                                    suffix={<span style={SUFFIX_STYLE}>%</span>}
                                    {...DECIMAL_NUMBER_INPUT_PROPS}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="taxCollectedOnSalesPercent"
                                label={<span className="smart-defaults-modal-label">Tax collected on sales</span>}
                                rules={[{ required: true, message: 'Required' }]}
                                extra={<FieldWarning message={taxCollectedWarning} />}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    className="w-100 products-table-edit-input"
                                    suffix={<span style={SUFFIX_STYLE}>%</span>}
                                    {...DECIMAL_NUMBER_INPUT_PROPS}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="smart-defaults-info-box">
                        <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: 16, marginTop: 2 }} />
                        <span>
                            Products you&apos;ve edited by hand keep their own values — unless you choose to overwrite them on save.
                        </span>
                    </div>

                    <div className="smart-defaults-modal-actions">
                        <button type="button" className="smart-defaults-cancel-btn" onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="submit" className="smart-defaults-save-btn" disabled={saving}>
                            {saving ? 'Saving…' : 'Save defaults'}
                        </button>
                    </div>
                </Form>
            </EditDefaultsModalBody>
        </Modal>
    );
};

export default EditDefaultsModal;
